import { LiveEventEvolutionScope } from '../../../live-events/components/helpers/liveEventEvolutionTimelineHelpers'
import { EventDialogTab } from '../../../live-events/hooks/useEventDialogTabs'
import { EModalType } from '../../../route-modals/types/modalTypes'

// Builders for links from Market Analytics into the customer Live Events calendar
// (`/live-events-tracker/games`). The calendar's blueprint/event modal has no blueprint-id
// URL param — it is addressed by a representative event (`m_liveEventId`) plus its event
// type (`m_liveEventTypeId`); the target tab decides whether it opens as a blueprint
// (evolution) view or a single-event view. Mirrors the URL shape used by the internal
// CustomerBlueprintLink component.

interface LiveEventsCalendarUrlParams {
  gameId: string
  // Opens the calendar on the days around this moment (epoch ms) instead of its default last-14-days view.
  focusDate?: number
}

// The calendar's default window is 14 days, so a focused one is as wide, centred on the focus date.
const CALENDAR_FOCUS_HALF_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

interface LiveEventsModalUrlParams {
  gameId: string
  eventTypeId: string
  eventId: string
  tab?: EventDialogTab
}

interface LiveEventsBlueprintModalUrlParams extends LiveEventsModalUrlParams {
  // Blueprint tab scope to open on. The tab opens on `all` (every event instance, the unit Live Event Analytics counts a blueprint's
  // events in) when this is omitted; pass `first` or `last` to open the one-row-per-version views instead.
  evolutionScope?: LiveEventEvolutionScope
}

interface LiveEventsEventModalUrlParams extends LiveEventsModalUrlParams {
  // Occurrence to open, as ms timestamps. Only the `liveEvent` modal reads these; without
  // them the dialog falls back to the event's latest occurrence.
  durationStart?: number
  durationEnd?: number
}

/** Base customer calendar for one game, grouped by blueprint (no modal), on the days around `focusDate` when given. */
export function buildLiveEventsCalendarUrl({ gameId, focusDate }: LiveEventsCalendarUrlParams): string {
  const focus = focusDate === undefined ? '' : `&dateFrom=${focusDate - CALENDAR_FOCUS_HALF_WINDOW_MS}&dateTo=${focusDate + CALENDAR_FOCUS_HALF_WINDOW_MS}`
  return `/live-events-tracker/games?gameIds=${encodeURIComponent(gameId)}&groupByBlueprint=true${focus}`
}

function buildLiveEventsModalUrl({
  gameId,
  eventTypeId,
  eventId,
  tab,
  durationStart,
  durationEnd,
  evolutionScope,
}: Required<LiveEventsModalUrlParams> &
  Pick<LiveEventsEventModalUrlParams, 'durationStart' | 'durationEnd'> &
  Pick<LiveEventsBlueprintModalUrlParams, 'evolutionScope'>): string {
  const hasDuration = durationStart !== undefined && durationEnd !== undefined
  return (
    `${buildLiveEventsCalendarUrl({ gameId })}&modalType=${hasDuration ? EModalType.LIVE_EVENT : EModalType.LIVE_EVENT_TYPE}` +
    `&m_trackedGameId=${encodeURIComponent(gameId)}` +
    `&m_liveEventTypeId=${encodeURIComponent(eventTypeId)}` +
    `&m_liveEventId=${encodeURIComponent(eventId)}` +
    (hasDuration ? `&m_durationStart=${durationStart}&m_durationEnd=${durationEnd}` : '') +
    (evolutionScope ? `&m_evolutionScope=${evolutionScope}` : '') +
    `&m_tab=${tab}`
  )
}

/** Calendar with the blueprint modal open on the evolution (blueprint) tab. */
export function buildLiveEventsBlueprintModalUrl({ tab = EventDialogTab.LiveEventEvolution, ...params }: LiveEventsBlueprintModalUrlParams): string {
  return buildLiveEventsModalUrl({ ...params, tab })
}

/** Calendar with the event modal open on the single-event description tab, on the given occurrence when a duration is passed. */
export function buildLiveEventsEventModalUrl({ tab = EventDialogTab.Description, ...params }: LiveEventsEventModalUrlParams): string {
  return buildLiveEventsModalUrl({ ...params, tab })
}
